/**
 * The dashboard's filter bar: the SHARED bar, handed a dashboard's answers.
 *
 * It is `report-kit`'s `FilterBar` — the one the report builder draws — so a
 * reader meets the same bar on both surfaces: the period first, a "+ Filter"
 * menu, chips that open the same pickers and carry the same is / is not
 * switch. What this file supplies is the three things that bar leaves to its
 * host, answered for a dashboard:
 *
 *  - DEFINITIONS: the union of every tile's filterable dimensions, from
 *    `deriveDashboardFilters` — see `filters.ts`, where that is decided once
 *    and tested without React.
 *  - LOADING and SAVING: the route's rules, which live in the URL. The bar
 *    reports an action; `dashboard-view.tsx` applies it with the same
 *    `applyFilterAction` the report builder's reducer uses.
 *  - PINS: the chips the dashboard's author pinned, stored on the definition
 *    as its filter decls. A pinned chip is on the bar for every reader, empty
 *    or not, with no ✕; everything else on the bar is the reader's own. While
 *    editing, every chip carries a pin toggle.
 *
 * It used to be a different bar, which showed a chip for EVERY dimension the
 * tiles shared and let nobody remove one — twenty chips on a dashboard of
 * three timesheet tiles. The author's half of that design (choosing which
 * chips belong) was never built; pinning is it.
 */

import { type FilterAction, FilterBar } from '@clockwise/report-kit';
import type { FilterNode, ReportPeriodPreset } from '@clockwise/semantic-core';
import { MAX_DASHBOARD_FILTERS } from '@clockwise/semantic-core';
import type { DateRangeValue } from '@clockwise/shared/date-range';
import { X } from 'lucide-react';
import { reportFilterControl } from '@/components/reports/report-filter-control';
// Binds report-kit's colour inputs to this app's theme — the chips' panels are
// the library's, and this page may be the first to mount one.
import '@/components/reports/report-kit-theme.css';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { FilterCandidate } from './filters';

interface Props {
  period: DateRangeValue;
  onPeriodChange: (next: DateRangeValue) => void;
  /** Resolved window, for the member lookups — they are period-scoped. */
  window: { from: string; to: string };
  /**
   * The periods the chip offers: the ones every tile's model offers
   * (`sharedPeriods`), since this one period drives them all. Every report
   * period when omitted.
   */
  periodPresets?: readonly ReportPeriodPreset[];
  /** What can be filtered. */
  fields: readonly FilterCandidate[];
  /** The reader's rules, from the URL. */
  filters: readonly FilterNode[];
  onAction: (action: FilterAction) => void;
  /** Take every filter off — the pinned chips stay, emptied. */
  onClear: () => void;
  /** Field ids the author pinned, in order. */
  pinned: readonly string[];
  /** Editing only: pin or unpin. */
  onPinnedChange?: (next: string[]) => void;
  /**
   * How many more may be pinned from THIS bar: the cap less any saved pins it
   * cannot show yet (their tile still loading, or a tombstone).
   */
  pinLimit?: number;
  /** Whether anything on the bar is narrowing the tiles. Drives the Clear button. */
  anySet: boolean;
  /**
   * The tiles have not all resolved, so `fields` is not yet the real set.
   *
   * Drawn as placeholders rather than as an empty bar: "no filters yet" and
   * "no filters at all" are different statements, and only one of them is true
   * here. See the derivation in `dashboard-view.tsx` for why the bar waits.
   */
  pending?: boolean;
  /** How many placeholders to draw while pending — the author's pins, which are known. */
  pendingChips?: number;
  /**
   * The bar could not be built at all, and waiting longer will not help.
   *
   * A THIRD state, not a flavour of the other two. An empty bar reads as "these
   * tiles share no filters" and placeholders read as "any moment now"; both are
   * claims about the dashboard, and neither is what happened here.
   */
  unavailable?: boolean;
}

export function DashboardFilterBar({
  period,
  onPeriodChange,
  window,
  periodPresets,
  fields,
  filters,
  onAction,
  onClear,
  pinned,
  onPinnedChange,
  pinLimit = MAX_DASHBOARD_FILTERS,
  anySet,
  pending = false,
  pendingChips = 0,
  unavailable = false,
}: Props) {
  return (
    <FilterBar
      fields={fields}
      filters={filters}
      onAction={onAction}
      period={{ value: period, window, onChange: onPeriodChange, presets: periodPresets }}
      // The report builder's own member picker: the same list, page size and
      // notion of which members a scoped caller may see, whichever surface
      // the Customer chip is on.
      renderControl={reportFilterControl}
      pins={{ pinned, onChange: onPinnedChange, limit: pinLimit }}
      // No menu until the fields are the real set — a menu offered before then
      // would gain entries under the pointer.
      canAdd={!pending && !unavailable}
      busy={pending}
      label=""
      // The page draws the frame; the builder's band padding is not wanted
      // here. `report-kit-tokens` binds the library's colours to ours without
      // `report-kit-scope`'s page layout.
      className="report-kit-tokens"
    >
      {unavailable ? (
        <p role="alert" className="text-xs text-destructive">
          Filters couldn’t load. Reload the page to try again.
        </p>
      ) : null}

      {pending ? (
        <>
          {/* `busy` on the bar is how the tiles in this folder mark a loading
              region, and this line is what actually reaches a reader: without
              it the bar announces as period-only. */}
          <span className="sr-only" role="status">
            Loading filters…
          </span>
          {Array.from({ length: pendingChips }, (_, i) => (
            <Skeleton key={i} className="h-[30px] w-36 rounded-full" aria-hidden="true" />
          ))}
        </>
      ) : null}

      {anySet && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1 text-muted-foreground"
          onClick={onClear}
        >
          <X className="size-3.5" aria-hidden="true" />
          Clear
        </Button>
      )}
    </FilterBar>
  );
}
